import React, { useEffect, useState } from "react";
import { MobileView } from "react-device-detect";
import { MobileWarningDialog } from "../common/MobileWarningDialog";
import { Sidenav } from "../ui/sidenav";
import { useKeycloak } from "@react-keycloak/web";
import { useGlobalStore } from "@/GlobalStore";
import { useLocation, useNavigate } from "react-router-dom";
import { buildRoute } from "@/router/constants";
import {
  RiBarChart2Fill,
  RiBrushAiFill,
  RiFlaskFill,
  RiSettings4Fill,
  RiVideoOnAiFill,
} from "@remixicon/react";
import WhatsNew, { WhatsNewPreview, shouldShowPreview } from "../common/whats-new/WhatsNew";

export type NAVBAR_ITEM = {
  key: string;
  icon: React.ReactNode;
  label: string;
  realm: string;
  description: string;
};
export const NavbarItems: Array<NAVBAR_ITEM> = [
  {
    key: "analyze",
    icon: <RiBarChart2Fill size={14} />,
    label: "Analyze",
    realm: "creative",
    description: "Analyze Creatives",
  },
  {
    key: "research",
    icon: <RiFlaskFill size={14} />,
    label: "Research",
    realm: "Insight",
    description: "Analyze Comments",
  },
  {
    key: "create",
    icon: <RiBrushAiFill size={14} />,
    label: "Create",
    realm: "creative",
    description: "Explore Creatives",
  },
  {
    key: "video-editor",
    icon: <RiVideoOnAiFill size={14} />,
    label: "Video Editor",
    realm: "Insight",
    description: "Edit Videos",
  },
  {
    key: "settings",
    icon: <RiSettings4Fill size={14} />,
    label: "Settings",
    realm: "Insight",
    description: "Manage Settings",
  },
];

const Navbar = () => {
  const [isSidenavOpen, setIsSidenavOpen] = useState<boolean>(false);
  const [mobileModal, setMobileModal] = useState(true);
  const { keycloak } = useKeycloak();
  const [isPylonHovered, setIsPylonHovered] = useState<boolean>(false);

  // Use selectors to subscribe only to needed state
  const permission = useGlobalStore((state) => state.permission);
  const selectedAccount = useGlobalStore((state) => state.selectedAccount);
  const accountList = useGlobalStore((state) => state.accountList);
  const setSelectedAccount = useGlobalStore((state) => state.setSelectedAccount);

  const { pathname } = useLocation();
  const [selectedTab, setSelectedTab] = useState<NAVBAR_ITEM | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const matchedTab = NavbarItems.find((tab) => pathname.includes(tab.key));
    if (matchedTab) setSelectedTab(matchedTab);
    else setSelectedTab(null);
  }, [pathname]);

  // Listen for hover on Pylon chat button to expand sidenav
  useEffect(() => {
    const handlePylonHover = () => {
      const pylonContainer = document.querySelector(".PylonChat-bubbleFrameContainer");
      if (pylonContainer) {
        pylonContainer.addEventListener("mouseenter", () => setIsPylonHovered(true));
        pylonContainer.addEventListener("mouseleave", () => setIsPylonHovered(false));
      }
    };

    // Pylon loads async, so we need to wait for it
    const observer = new MutationObserver(() => {
      const pylonContainer = document.querySelector(".PylonChat-bubbleFrameContainer");
      if (pylonContainer) {
        handlePylonHover();
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    handlePylonHover(); // Try immediately in case it's already loaded

    return () => observer.disconnect();
  }, []);

  const onTabClick = (navbarItem: NAVBAR_ITEM) => {
    const accountId = selectedAccount ? selectedAccount?.id : accountList?.[0]?.id;
    if (!selectedAccount && accountList?.[0]) setSelectedAccount(accountList?.[0]); // if account is not selected setting selected account for
    // Get saved last URL for this tab
    const getLastUrl = useGlobalStore.getState().getLastUrl;
    const lastUrl = getLastUrl(navbarItem.key);
    const safeUrl = accountId && lastUrl?.includes(accountId) ? lastUrl : null;

    switch (navbarItem.key) {
      case "analyze":
        navigate(safeUrl || buildRoute.analyze(accountId));
        break;
      case "research":
        navigate(safeUrl || buildRoute.research(accountId));
        break;
      case "create":
        navigate(safeUrl || buildRoute.create(accountId));
        break;
      case "settings":
        navigate(safeUrl || buildRoute.settings(accountId));
        break;
      case "video-editor":
        navigate(buildRoute.video_editor()); // No url persistence, sidebar is different
        break;
      default:
        navigate("/");
    }
  };

  const [anyNavbarDialogOpen, setAnyNavbarDialogOpen] = useState<boolean>(false);

  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(() => shouldShowPreview());

  return (<>
    <div
      className="fixed top-0 left-0 z-[40] flex flex-col h-dvh pl-1 py-1"
      onMouseEnter={() => setIsSidenavOpen(true)}
      onMouseLeave={() => {
        if (!anyNavbarDialogOpen) setIsSidenavOpen(false);
      }}
    >
      <MobileView>
        <MobileWarningDialog open={mobileModal} onClose={() => setMobileModal(false)} />
      </MobileView>
      <Sidenav
        tabs={NavbarItems.filter((item) => {
          if (!keycloak.hasRealmRole(item.realm)) {
            return false;
          }
          if (item.key === "video-editor") {
            return permission.video_editor === true;
          }
          return true;
        })}
        onTabClick={onTabClick}
        selectedTab={selectedTab}
        isOpen={isSidenavOpen || isPylonHovered}
        onAnyDialogOpenChange={(open) => {
          setAnyNavbarDialogOpen(open);
          if (!open)
            setTimeout(() => {
              setIsSidenavOpen(false);
            }, 500);
        }}
        whatsNewOpen={whatsNewOpen}
        onWhatsNewToggle={() => {
          setWhatsNewOpen((prev) => !prev);
          setPreviewOpen(false);
        }}
      />
    </div>
    <WhatsNew
      open={whatsNewOpen}
      onClose={() => setWhatsNewOpen(false)}
    />
    {!whatsNewOpen && (
      <WhatsNewPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onOpenWhatsNew={() => {
          setPreviewOpen(false);
          setWhatsNewOpen(true);
        }}
      />
    )}
  </>
  );
};

export default Navbar;
